import { CancellationReason, PlanType, type Prisma } from "@brookbush/database";

import type Stripe from "stripe";
import z from "zod";

export enum TrialLength {
	MONTH = "month",
	WEEK = "week",
	DAY = "day",
}

const planTypeDiscriminatedUnion = z.discriminatedUnion("planType", [
	z.object({
		planType: z.literal(PlanType.INDIVIDUAL),
	}),
	z.object({
		planType: z.literal(PlanType.TEAM),
		quantity: z.number().gte(2).lte(35),
	}),
]);

const planConfig = z
	.object({
		priceId: z.string(),
		promoCodeId: z.string().optional(),
		trialLength: z.enum(TrialLength).optional(),
		currency: z.string().optional(),
	})
	.and(planTypeDiscriminatedUnion);

export const createSubscriptionSchema = z.object({
	planConfig,
	paymentMethodId: z.string().optional(),
	path: z.string().optional(),
	ipAddress: z.string().optional(),
	userAgent: z.string().optional(),
	ctaClick: z.string().optional(),
	referrerId: z.string().optional(),
	/** Pricing-model experiment arm stored on the Stripe subscription. */
	pricingModel: z.string().optional(),
	/** Pricing-model experiment variant stored on the Stripe subscription. */
	pricingVariant: z.string().optional(),
});
export type CreateSubscriptionSchema = z.infer<typeof createSubscriptionSchema>;

export const createSubscriptionInvoiceSchema = z.object({
	planConfig: z
		.object({
			priceId: z.string(),
			promoCodeId: z.string().optional(),
			trialLength: z.enum(TrialLength).optional(),
		})
		.and(planTypeDiscriminatedUnion),
	paymentMethodId: z.string().optional(),
	path: z.string().optional(),
	ipAddress: z.string().optional(),
	userAgent: z.string().optional(),
	ctaClick: z.string().optional(),
	/** Pricing-model experiment arm stored on the Stripe subscription. */
	pricingModel: z.string().optional(),
	/** Pricing-model experiment variant stored on the Stripe subscription. */
	pricingVariant: z.string().optional(),
});
export type CreateSubscriptionInvoiceSchema = z.infer<
	typeof createSubscriptionInvoiceSchema
>;

export const updateSubscriptionSchema = z
	.object({
		priceId: z.string(),
		promoCodeId: z.string().optional(),
	})
	.and(planTypeDiscriminatedUnion);

export type UpdateSubscriptionSchema = z.infer<typeof updateSubscriptionSchema>;

export const subscriptionProrationsSchema = planConfig;

export type SubscriptionProrationsSchema = z.infer<
	typeof subscriptionProrationsSchema
>;

export type PlanConfig = {
	priceId?: string;
	promoCodeId?: string;
	currency?: string;
	trialLength?: TrialLength;
	planType: PlanType;
} & (
	| {
			planType: "INDIVIDUAL";
	  }
	| {
			planType: "TEAM";
			quantity: number;
	  }
);

export type PlanConfigExpanded = PlanConfig & {
	price?: Price;
	promoCode?: Stripe.PromotionCode;
};

export enum PlanInterval {
	MONTH = "month",
	YEAR = "year",
}

export interface SubscriptionExpanded
	extends Prisma.SubscriptionGetPayload<{
		include: {
			organization: true;
			user: true;
		};
	}> {
	planInterval?: PlanInterval;
}

export enum CheckoutStatus {
	IDLE = "IDLE",
	IN_PROGRESS = "IN_PROGRESS",
	SUCCESSFUL = "SUCCESSFUL",
}

export const cancellationSurveyFormSchema = z.object({
	reason: z.enum(CancellationReason, {
		message: "Required",
	}),
	otherReason: z.string().optional(),

	satisfactionRating: z
		.number({
			message: "Required",
		})
		.int()
		.min(1)
		.max(5),
	reconsiderationReason: z.string().optional(),
	additionalFeedback: z.string().optional(),
});

export type CancellationSurveyFormSchema = z.infer<
	typeof cancellationSurveyFormSchema
>;

export const COUNTRY_CODE_TO_CURRENCY: {
	[key: string]: string;
} = {
	US: "usd", // United States
	CA: "cad", // Canada
	AU: "aud", // Australia
	GB: "gbp", // United Kingdom
	NZ: "nzd", // New Zealand

	AE: "aed", // United Arab Emirates
	SA: "sar", // Saudi Arabia
	EG: "egp", // Egypt

	IN: "inr", // India
	PK: "pkr", // Pakistan

	PH: "php", // Philippines

	FR: "eur", // France
	DE: "eur", // Germany
	IT: "eur", // Italy
	ES: "eur", // Spain
	NL: "eur", // Netherlands
	BE: "eur", // Belgium
	AT: "eur", // Austria
	PT: "eur", // Portugal
	IE: "eur", // Ireland
	FI: "eur", // Finland
	GR: "eur", // Greece
	SK: "eur", // Slovakia
	SI: "eur", // Slovenia
	LT: "eur", // Lithuania
	LV: "eur", // Latvia
	EE: "eur", // Estonia
	CY: "eur", // Cyprus
	MT: "eur", // Malta
	LU: "eur", // Luxembourg

	ME: "eur", // Montenegro (uses EUR unofficially)
	XK: "eur", // Kosovo (uses EUR unofficially)
	VA: "eur", // Vatican City
	AD: "eur", // Andorra
	MC: "eur", // Monaco
	SM: "eur", // San Marino
};

export type Price = Stripe.Price & {
	paypalPlanId?: string; // Optional field for PayPal plan ID
	/** Regular-cycle amount the mapped PayPal plan charges, in cents. */
	paypalPlanAmount?: number;
};

export interface PayPalPlan {
	id: string;
	product_id: string;
	name: string;
	description?: string;
	status: "ACTIVE" | "INACTIVE" | "CREATED";
	usage_type?: "LICENSED" | "SERVICE";
	billing_cycles: {
		tenure_type: "TRIAL" | "REGULAR";
		sequence: number;
		total_cycles?: number; // trial may omit
		frequency: {
			interval_unit: "DAY" | "WEEK" | "MONTH" | "YEAR";
			interval_count: number;
		};
		pricing_scheme: {
			version?: number;
			fixed_price: {
				currency_code: string;
				value: string;
			};
			tiers?: Array<{
				starting_quantity: string;
				ending_quantity?: string;
				amount: { currency_code: string; value: string };
			}>;
		};
		create_time?: string;
		update_time?: string;
	}[];
	payment_preferences: {
		auto_bill_outstanding: boolean;
		setup_fee?: { currency_code: string; value: string };
		setup_fee_failure_action: "CONTINUE" | "CANCEL";
		payment_failure_threshold: number;
	};
	taxes?: { percentage: string; inclusive: boolean };
	quantity_supported?: boolean;
	create_time: string;
	update_time: string;
	links: Array<{
		href: string;
		rel: string;
		method: string;
	}>;
}

export interface PayPalWebhookEvent {
	id: string;
	event_version: string;
	create_time: string;
	resource_type: string;
	event_type:
		| "BILLING.SUBSCRIPTION.PAYMENT.FAILED"
		| "BILLING.SUBSCRIPTION.ACTIVATED"
		| "BILLING.SUBSCRIPTION.CANCELLED"
		| "PAYMENT.SALE.COMPLETED";

	summary: string;
	resource: unknown;
	links: {
		href: string;
		rel: string;
		method: string;
	}[];
}

export interface PayPalSubscription {
	id: string;
	plan_id: string;
	start_time: string;
	quantity: string;
	plan: PayPalPlan;
	shipping_amount?: {
		currency_code: string;
		value: string;
	};
	custom_id: string;
	subscriber: {
		name: {
			given_name: string;
			surname: string;
		};
		email_address: string;
		payer_id: string;
		shipping_address?: {
			name: {
				full_name: string;
			};
			address: {
				address_line_1: string;
				address_line_2?: string;
				admin_area_2: string;
				admin_area_1: string;
				postal_code: string;
				country_code: string;
			};
		};
	};
	billing_info: {
		outstanding_balance: {
			currency_code: string;
			value: string;
		};
		cycle_executions: Array<{
			tenure_type: string;
			sequence: number;
			cycles_completed: number;
			cycles_remaining: number;
			total_cycles: number;
		}>;
		last_payment?: {
			amount: {
				currency_code: string;
				value: string;
			};
			time: string;
		};
		next_billing_time: string;
		failed_payments_count: number;
	};
	status:
		| "APPROVAL_PENDING"
		| "APPROVED"
		| "ACTIVE"
		| "SUSPENDED"
		| "CANCELLED"
		| "EXPIRED";
	status_update_time: string;
	create_time: string;
	update_time: string;
	links: Array<{
		href: string;
		rel: string;
		method: string;
	}>;
}

export interface PayPalSubscriptionTransaction {
	id: string;
	status: "COMPLETED" | "DECLINED" | "PENDING" | string;
	status_details?: {
		reason: string;
	};
	amount_with_breakdown: {
		gross_amount: {
			currency_code: string;
			value: string;
		};
		fee_amount?: {
			currency_code: string;
			value: string;
		};
		net_amount?: {
			currency_code: string;
			value: string;
		};
		tax_amount?: {
			currency_code: string;
			value: string;
		};
		shipping_amount?: {
			currency_code: string;
			value: string;
		};
	};

	payer_name?: {
		given_name?: string;
		surname?: string;
		full_name?: string;
	};
	payer_email?: string;
	time: string; // ISO8601 timestamp
}
